import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Lock, MessageSquareText, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money } from "@/data/products";

const RequestRow = ({ r, onApprove }) => {
  const [busy, setBusy] = useState(false);
  const approved = r.status === "approved";
  const expired = approved && r.code_expires_at && new Date(r.code_expires_at) < new Date();

  const approve = async () => {
    if (approved || busy) return;
    setBusy(true);
    try {
      await onApprove(r.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`delivery-request-row-${r.id}`} className="flex flex-wrap items-center gap-4 py-4 border-b border-[#181203]/15">
      <input
        type="checkbox"
        data-testid={`approve-checkbox-${r.id}`}
        checked={approved}
        onChange={approve}
        disabled={approved || busy}
        className="w-6 h-6 accent-[#2E7D32] cursor-pointer disabled:cursor-not-allowed"
        aria-label={`Approve delivery setup for ${r.name}`}
      />
      <div className="min-w-0">
        <p className="font-display font-bold">{r.name}</p>
        <p data-testid={`request-phone-${r.id}`} className="text-sm text-[#594519]">📞 {r.phone} · {r.address}</p>
      </div>
      {approved ? (
        <span data-testid={`code-display-${r.id}`} className="ml-auto font-display text-2xl font-extrabold tracking-[0.3em] text-[#2E7D32]">
          {expired ? "EXPIRED" : r.code}
        </span>
      ) : (
        <span className="ml-auto text-xs font-bold uppercase tracking-widest text-[#B5122F]">Awaiting approval</span>
      )}
    </li>
  );
};

export default function Admin() {
  const [pass, setPass] = useState(sessionStorage.getItem("sj_admin") || "");
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);

  const headers = { "X-Admin-Passcode": pass };

  const load = async () => {
    try {
      const [r, o, c] = await Promise.all([
        api.get("/admin/delivery-requests", { headers }),
        api.get("/admin/orders", { headers }),
        api.get("/admin/contact-messages", { headers }),
      ]);
      setRequests(r.data);
      setOrders(o.data);
      setMessages(c.data);
      setOk(true);
      setError("");
    } catch (err) {
      if (err?.response?.status === 401) { setOk(false); setError("Wrong passcode."); }
    }
  };

  useEffect(() => {
    if (!ok) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  });

  const approve = async (id) => {
    await api.post(`/admin/delivery-requests/${id}/approve`, {}, { headers });
    await load();
  };

  if (!ok) {
    return (
      <main data-testid="admin-login" className="min-h-screen bg-[#181203] grid place-items-center px-5">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="w-full max-w-sm bg-[#FFF4D6] rounded-3xl p-8 space-y-4"
        >
          <Lock className="w-8 h-8" />
          <h1 className="font-display text-3xl font-extrabold">Owner approvals</h1>
          <input
            data-testid="admin-passcode-input"
            type="password"
            placeholder="Admin passcode"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            className="w-full h-12 rounded-xl border-2 border-[#181203]/30 bg-white/60 px-4 outline-none focus:border-[#181203]"
          />
          {error && <p data-testid="admin-error" className="text-sm font-bold text-[#B5122F]">{error}</p>}
          <button data-testid="admin-login-button" className="w-full h-12 rounded-full bg-[#181203] text-[#FFD000] font-bold">Enter</button>
        </form>
      </main>
    );
  }

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FFF4D6] pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-5 md:px-10">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Owner approvals</h1>
        <p className="mt-2 text-[#594519] flex items-center gap-2"><KeyRound className="w-4 h-4" /> Check the box to confirm delivery is set up — a 5-digit code (valid 24 hours) is issued for you to text the customer.</p>
        <h2 className="font-display text-2xl font-bold mt-10 mb-2">Delivery requests <span data-testid="pending-count" className="text-[#B5122F]">({pending.length} pending)</span></h2>
        <ul data-testid="delivery-requests-list">
          {requests.length === 0 && <p data-testid="no-requests" className="py-8 text-center text-[#594519]">No delivery requests yet.</p>}
          {requests.map((r) => <RequestRow key={r.id} r={r} onApprove={approve} />)}
        </ul>
        <h2 className="font-display text-2xl font-bold mt-12 mb-2">Orders</h2>
        <ul data-testid="orders-list" className="space-y-3">
          {orders.length === 0 && <p data-testid="no-orders" className="py-6 text-center text-[#594519]">No orders yet.</p>}
          {orders.map((o) => (
            <motion.li layout key={o.id} data-testid={`order-row-${o.id}`} className="bg-[#FFFDF4] rounded-2xl border border-[#181203]/10 p-4">
              <div className="flex justify-between gap-4 font-bold">
                <span>#{String(o.id).slice(-6)} · {o.name} · {o.phone}</span>
                <span>{money(o.subtotal)}</span>
              </div>
              <p className="text-sm text-[#594519] mt-1">
                {o.items.map((i) => `${i.qty}× ${i.product_id.replace(/-/g, " ")}${i.addons.length ? ` (+${i.addons.join(", ")})` : ""}`).join(" · ")}
              </p>
              <p className="text-sm text-[#594519]">Ship to: {o.address}</p>
            </motion.li>
          ))}
        </ul>
        <h2 className="font-display text-2xl font-bold mt-12 mb-2 flex items-center gap-2"><Mail className="w-5 h-5" /> Contact messages</h2>
        <ul data-testid="messages-list" className="space-y-3">
          {messages.length === 0 && <p data-testid="no-messages" className="py-6 text-center text-[#594519]">No contact messages yet.</p>}
          {messages.map((m) => (
            <motion.li layout key={m.id} data-testid={`message-row-${m.id}`} className="bg-[#FFFDF4] rounded-2xl border border-[#181203]/10 p-4">
              <div className="flex justify-between gap-4 font-bold">
                <span>{m.name} · {m.subject}</span>
                <span className="text-xs font-semibold text-[#594519]">{String(m.created_at).slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="text-sm text-[#594519]">{m.email}</p>
              <p className="mt-2 text-sm whitespace-pre-wrap">{m.message}</p>
              <a href={`mailto:${m.email}`} data-testid={`reply-message-${m.id}`} className="mt-2 inline-block text-xs font-bold underline underline-offset-4">Reply by email</a>
            </motion.li>
          ))}
        </ul>
      </div>
    </main>
  );
}
